import { useId, useRef, useState } from "react";
import { Button, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import IconLoader from "@/features/Common/components/IconLoader";
import Panel from "@/features/Common/components/Panel";
import theme, {
  defaultFullscreenPageStyling,
  overrideDefaultLinkStyling,
} from "@/theme";
import { useNativeHealthSDK } from "@/hooks/useNativeHealthSDK";
import { convertPxToRem, isNativeApp } from "@/utils";

/** Extracts the origin from a URL string, returning undefined for missing or malformed values. */
function getOrigin(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
}

function VersionRow({
  label,
  value,
  chip = false,
}: {
  label: string;
  value: string;
  chip?: boolean;
}) {
  return (
    <Stack
      direction={chip ? "row" : { xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems="flex-start"
      gap={chip ? 2 : { xs: 0.5, sm: 2 }}
      sx={{ minWidth: 0 }}
    >
      <Typography
        variant="body2"
        sx={{ minWidth: 0, flex: "1 1 45%", overflowWrap: "anywhere" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight="bold"
        sx={{
          minWidth: 0,
          maxWidth: chip ? "60%" : { xs: "100%", sm: "60%" },
          textAlign: chip ? "right" : { xs: "left", sm: "right" },
          overflowWrap: "anywhere",
          fontVariantNumeric: "tabular-nums",
          ...(chip && {
            backgroundColor: theme.palette.neutral[100],
            color: theme.palette.neutral[700],
            borderRadius: convertPxToRem(4),
            px: 1,
            py: 0.25,
          }),
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function AboutApp() {
  const { t } = useTranslation();
  const appVersion = import.meta.env.VITE_APP_VERSION;
  const webVersions = __WEB_PACKAGE_VERSIONS__;
  const native = isNativeApp();
  const appVersionID = useId();
  const { status } = useNativeHealthSDK();

  const envRows = [
    {
      key: "mainApi",
      origin: getOrigin(import.meta.env.VITE_APP_UNU_HEALTH_API_BASE_URI),
    },
    {
      key: "notificationsApi",
      origin: getOrigin(
        import.meta.env.VITE_APP_UNU_HEALTH_NOTIFICATIONS_API_BASE_URI
      ),
    },
    {
      key: "chronicCareApi",
      origin: getOrigin(
        import.meta.env.VITE_APP_UNU_HEALTH_CHRONIC_CARE_API_BASE_URI
      ),
    },
    {
      key: "logAQueryApi",
      origin: getOrigin(
        import.meta.env.VITE_APP_UNU_HEALTH_LOG_A_QUERY_ENDPOINT
      ),
    },
  ] as const;
  const hasAnyEnvRow = envRows.some((r) => r.origin);

  const clickCountRef = useRef(0);
  const [devMode, setDevMode] = useState(false);
  const [copyState, setCopyState] = useState<
    "idle" | "copying" | "copied" | "failed"
  >("idle");
  const notAvailable = t("AboutApp.notAvailable");
  const notReported = t("AboutApp.notReported");
  const buildRows = [
    {
      label: t("AboutApp.build.appVersion"),
      value: appVersion?.split("+")[0] || notAvailable,
    },
    {
      label: t("AboutApp.build.identifier"),
      value: appVersion?.includes("+") ? appVersion : notAvailable,
    },
    ...(native
      ? [
          {
            label: t("AboutApp.build.nativeVersion"),
            value: status?.nativeAppVersion || notReported,
          },
          {
            label: t("AboutApp.build.nativeBuild"),
            value: status?.nativeBuildNumber || notReported,
          },
        ]
      : []),
  ];
  const packageGroups = [
    {
      label: t("AboutApp.packages.web"),
      rows: [
        { label: "Axios", value: webVersions.axios },
        { label: "Binah SDK", value: webVersions.binah },
        { label: "React", value: webVersions.react },
        { label: "React Query", value: webVersions.reactQuery },
        { label: "XState", value: webVersions.xstate },
        { label: "Firebase", value: webVersions.firebase },
      ],
    },
    ...(native
      ? [
          {
            label: t("AboutApp.packages.native"),
            rows: [
              {
                label: "Axios",
                value: status?.nativePackageVersions?.axios || notReported,
              },
              {
                label: "Binah SDK",
                value:
                  status?.nativePackageVersions?.binah ||
                  status?.binahNativeSdkVersion ||
                  notReported,
              },
              {
                label: "React Native",
                value:
                  status?.nativePackageVersions?.reactNative || notReported,
              },
              {
                label: "Expo",
                value: status?.nativePackageVersions?.expo || notReported,
              },
              {
                label: "WebView",
                value: status?.nativePackageVersions?.webView || notReported,
              },
              {
                label: "Firebase Messaging",
                value:
                  status?.nativePackageVersions?.firebaseMessaging ||
                  notReported,
              },
            ],
          },
        ]
      : []),
  ];

  async function copyVersions() {
    setCopyState("copying");

    const text = [
      t("AboutApp.build.title"),
      ...buildRows.map(({ label, value }) => `${label}: ${value}`),
      "",
      t("AboutApp.packages.title"),
      ...packageGroups.flatMap(({ label, rows }) => [
        label,
        ...rows.map((row) => `${row.label}: ${row.value}`),
        "",
      ]),
    ]
      .join("\n")
      .trim();
    try {
      await navigator.clipboard.writeText(text);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  function onVersionPanelClick() {
    clickCountRef.current += 1;
    if (clickCountRef.current === 10) {
      setDevMode(true);
      clickCountRef.current = 0;
    }
  }

  return (
    <Stack spacing={3} sx={{ ...defaultFullscreenPageStyling }}>
      <CircledBackButton showLabel={true} />
      <Stack my={3}>
        <Typography variant="h2">{t("AboutApp.title")}</Typography>
      </Stack>

      <Panel>
        <Stack
          spacing={1}
          onClick={onVersionPanelClick}
          component="button"
          type="button"
          sx={{
            border: 0,
            p: 0,
            width: "100%",
            textAlign: "left",
            background: "transparent",
            color: "inherit",
            cursor: "pointer",
          }}
        >
          <Typography
            id={appVersionID}
            component="span"
            variant="h5"
            color={theme.palette.neutral[500]}
          >
            {t("AboutApp.sections.version.label")}
          </Typography>
          <Typography
            aria-labelledby={appVersionID}
            component="span"
            fontWeight="bold"
            sx={{ overflowWrap: "anywhere" }}
          >
            {appVersion?.split("+")[0]}
          </Typography>
        </Stack>
      </Panel>

      {devMode && (
        <>
          <Panel>
            <Stack spacing={2}>
              <Typography variant="h5" color={theme.palette.neutral[500]}>
                {t("AboutApp.build.title")}
              </Typography>
              {buildRows.map((row) => (
                <VersionRow key={row.label} {...row} />
              ))}
            </Stack>
          </Panel>
          <Panel>
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                flexWrap="wrap"
              >
                <Typography variant="h5" color={theme.palette.neutral[500]}>
                  {t("AboutApp.packages.title")}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    void copyVersions();
                  }}
                  disabled={copyState === "copying"}
                >
                  {t("AboutApp.copyVersions")}
                </Button>
              </Stack>
              {packageGroups.map((group, index) => (
                <Stack
                  key={group.label}
                  spacing={1.5}
                  component="section"
                  aria-label={group.label}
                >
                  {index > 0 && (
                    <Divider sx={{ borderColor: theme.palette.neutral[300] }} />
                  )}
                  <Typography
                    variant="body2"
                    color={theme.palette.neutral[500]}
                  >
                    {group.label}
                  </Typography>
                  {group.rows.map((row) => (
                    <VersionRow key={row.label} {...row} chip />
                  ))}
                </Stack>
              ))}
              <Typography
                role="status"
                variant="body2"
                color={copyState === "failed" ? "error" : "text.secondary"}
              >
                {copyState === "copied"
                  ? t("AboutApp.copied")
                  : copyState === "failed"
                    ? t("AboutApp.copyFailed")
                    : ""}
              </Typography>
            </Stack>
          </Panel>
        </>
      )}

      {devMode && hasAnyEnvRow && (
        <Panel>
          <Stack spacing={1}>
            <Typography variant="h5" color={theme.palette.neutral[500]}>
              {t("AboutApp.sections.devEnvironment.title")}
            </Typography>

            {envRows.map(
              ({ key, origin }) =>
                origin && (
                  <VersionRow
                    key={key}
                    label={t(`AboutApp.sections.devEnvironment.${key}`)}
                    value={origin}
                  />
                )
            )}
          </Stack>
        </Panel>
      )}

      <Panel>
        <Stack spacing={1}>
          <Typography variant="h5" color={theme.palette.neutral[500]}>
            {t("AboutApp.sections.legalInformation.label")}
          </Typography>
          <Typography variant="body2">
            {t("AboutApp.sections.legalInformation.content")}
          </Typography>
        </Stack>
      </Panel>

      <Panel padding={0}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          p={2}
          sx={{ ...overrideDefaultLinkStyling }}
          component="a"
          href="/home/help/about-app/open-source-licenses"
        >
          <Typography variant="h4">
            {t("AboutApp.sections.openSourceLicenses.linkLabel")}
          </Typography>
          <IconLoader icon="ChevronNextIcon" />
        </Stack>
      </Panel>
    </Stack>
  );
}
